import { Check, CheckCheck, AlertCircle, FileText, MapPin, User, Reply, Download, ExternalLink } from "lucide-react";
import { mediaUrl, fmtTime } from "./util";

function Ticks({ status }) {
  if (status === "failed") return <AlertCircle className="w-3.5 h-3.5 text-rose-500" />;
  if (status === "read") return <CheckCheck className="w-4 h-4 text-[#53bdeb]" />;
  if (status === "delivered") return <CheckCheck className="w-4 h-4 text-[#8696a0]" />;
  return <Check className="w-4 h-4 text-[#8696a0]" />;
}

const LABEL_FAIL = { image: "Photo", sticker: "Sticker", video: "Video", audio: "Voice message", document: "Document" };

function Media({ m }) {
  const md = m.media || {};
  const url = mediaUrl(m.id);
  if (md.error && !md.storage_path)
    return <div className="text-xs text-[#667781] italic px-3 py-5 bg-black/5 rounded-md min-w-[200px]" data-testid="inbox-msg-media-failed">{LABEL_FAIL[m.type] || "File"} could not be loaded</div>;
  if (m.type === "image" || m.type === "sticker")
    return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={m.caption || "Photo"} className={`rounded-md ${m.type === "sticker" ? "w-32 h-32 object-contain" : "max-w-[280px] max-h-[320px] object-cover"}`} data-testid="inbox-msg-image"
      onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("div"), { className: "text-xs text-[#667781] italic px-2 py-6 bg-black/5 rounded-md", textContent: "Photo could not be loaded" })); }} /></a>;
  if (m.type === "video") return <video src={url} controls className="rounded-md max-w-[280px]" />;
  if (m.type === "audio") return <audio src={url} controls className="max-w-[260px]" data-testid="inbox-msg-audio" />;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-3 bg-black/5 rounded-md px-3 py-2.5 min-w-[220px]" data-testid="inbox-msg-document">
      <FileText className="w-8 h-8 text-rose-500 flex-shrink-0" />
      <span className="flex-1 min-w-0 text-sm truncate">{md.filename || "Document"}</span>
      <Download className="w-4 h-4 text-[#54656f]" />
    </a>
  );
}

function Body({ m }) {
  if (m.type === "location" && m.location)
    return (
      <a href={`https://maps.google.com/?q=${m.location.latitude},${m.location.longitude}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#027eb5] text-sm">
        <MapPin className="w-5 h-5" /> {m.text || "Shared location"} <ExternalLink className="w-3 h-3" />
      </a>
    );
  if (m.type === "contacts")
    return (m.contacts || []).map((c, i) => (
      <div key={i} className="flex items-center gap-2 text-sm"><User className="w-5 h-5 text-[#54656f]" /><span><b>{c.name}</b> {c.phones?.join(", ")}</span></div>
    ));
  return null;
}

export function Bubble({ m, onReply }) {
  const out = m.direction === "out";
  const hasMedia = m.media && m.type !== "template";
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"} group px-[6%] mb-1`} data-testid={`inbox-msg-${m.direction}`}>
      {out && m.wamid && <button onClick={() => onReply(m)} className="opacity-0 group-hover:opacity-100 self-center mr-1 text-[#8696a0] hover:text-[#54656f] transition-opacity" title="Reply" data-testid="inbox-msg-reply-out-btn"><Reply className="w-4 h-4" /></button>}
      <div className={`relative max-w-[min(520px,80%)] rounded-lg px-2.5 pt-1.5 pb-1 shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] ${out ? "bg-[#d9fdd3] rounded-tr-none" : "bg-white rounded-tl-none"}`}>
        {m.type === "template" && <div className="text-[11px] font-semibold text-[#008069] mb-1">Template · {m.template_name}{m.source && m.source !== "inbox" ? ` · ${m.source}` : ""}</div>}
        {m.context && (
          <div className={`mb-1 rounded-md border-l-4 px-2 py-1 text-xs ${m.context.direction === "out" ? "border-[#06cf9c] bg-black/5" : "border-[#53bdeb] bg-black/5"}`}>
            <div className="font-semibold text-[#008069]">{m.context.direction === "out" ? "You" : "Parent"}</div>
            <div className="text-[#667781] line-clamp-2">{m.context.text}</div>
          </div>
        )}
        {hasMedia && <div className="mb-1"><Media m={m} /></div>}
        <Body m={m} />
        {(m.text || m.caption) && m.type !== "location" && <div className="text-[14.2px] text-[#111b21] whitespace-pre-wrap break-words" data-testid="inbox-msg-text">{m.type === "template" ? m.text : (m.text || m.caption)}</div>}
        {m.type === "template" && !m.text && <div className="text-sm italic text-[#667781]">Template message</div>}
        {m.buttons?.length > 0 && <div className="mt-1.5 border-t border-black/10 pt-1 space-y-0.5">{m.buttons.map((b, i) => <div key={i} className="text-center text-[13px] text-[#027eb5] py-0.5">{b}</div>)}</div>}
        <div className="flex items-center justify-end gap-1 mt-0.5 -mb-0.5">
          {out && m.sent_by && <span className="text-[10px] text-[#667781] mr-1">{m.sent_by}</span>}
          <span className="text-[11px] text-[#667781]">{fmtTime(m.at)}</span>
          {out && <Ticks status={m.status} />}
        </div>
        {m.status === "failed" && m.error && <div className="text-[11px] text-rose-600 mt-0.5 max-w-[300px]">{m.error}</div>}
        {m.reaction && <span className="absolute -bottom-3 right-2 bg-white rounded-full px-1.5 text-sm shadow">{m.reaction}</span>}
      </div>
      {!out && m.wamid && <button onClick={() => onReply(m)} className="opacity-0 group-hover:opacity-100 self-center ml-1 text-[#8696a0] hover:text-[#54656f] transition-opacity" title="Reply" data-testid="inbox-msg-reply-btn"><Reply className="w-4 h-4" /></button>}
    </div>
  );
}
